/**
 * SPDX-FileCopyrightText: 2020-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

/**
 * Dynamic theming service for applying the application's platform-level theme to the rendered document.
 *
 * @param {Object} [theme]
 * @param {string} [theme.title] - the page title
 * @param {Object} [theme.favicon] - the page favicon (with href and type)
 * @param {Object[]} [theme.linkElements] - list of objects with properties being valid attributes of
 *                                          https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/link
 * @returns {Object} the theming service
 */
export default function themingServiceFactory({ title, favicon, linkElements = [] } = {}) {
    const faviconDataSource = 'platform-theme-favicon';
    const linkElementsDataSource = 'platform-theme-link-elements';

    return {
        /**
         * Update the page title
         */
        updateTitle() {
            if (title && title.length) {
                document.title = title;
            }
        },

        /**
         * Update the page favicon
         */
        updateFavicon() {
            if (favicon && favicon.href) {
                const head = document.head;
                //remove all favicon first
                for (let currentIcon of head.querySelectorAll(`link[data-source=${faviconDataSource}]`)) {
                    head.removeChild(currentIcon);
                }
                const link = document.createElement('link');
                link.rel = 'shortcut icon';
                link.href = favicon.href;
                link.type = favicon.type || 'image/x-icon';
                link.dataset.source = faviconDataSource;
                head.appendChild(link);
            }
        },

        /**
         * Remove the elements injected by injectLinkElements()
         */
        removeLinkElements() {
            const head = document.head;
            for (let linkElt of head.querySelectorAll(`link[data-source=${linkElementsDataSource}]`)) {
                head.removeChild(linkElt);
            }
        },

        /**
         * Create and inject <head><link> tags for all the elements defined in this theme as 'linkElements'.
         * Main use cases are: stylesheets; fonts;
         */
        injectLinkElements() {
            if (!linkElements) {
                return;
            }
            linkElements.forEach(link => {
                if (!link.rel || !link.href) {
                    throw new Error(
                        'Invalid platform theme linkElement defined. linkElements must contain rel and href.'
                    );
                }
                const linkElt = document.createElement('link');
                linkElt.rel = link.rel;
                linkElt.href = link.href;
                linkElt.dataset.source = linkElementsDataSource;

                if (link.type) {
                    linkElt.type = link.type;
                }
                if (link.as) {
                    linkElt.as = link.as;
                    if (['fetch', 'font'].includes(link.as)) {
                        linkElt.crossOrigin = 'anonymous';
                    }
                }
                // note intentional casing difference between element's HTML attribute and JS property
                if (link.crossorigin) {
                    linkElt.crossOrigin = link.crossorigin;
                }
                document.head.appendChild(linkElt);
            });
        },

        /**
         * Load the theme
         * @returns {Promise}
         */
        load() {
            this.updateTitle();
            this.updateFavicon();
            this.removeLinkElements();
            this.injectLinkElements();

            return Promise.resolve();
        }
    };
}
